type Status = 'success' | 'warning' | 'error' | 'info' | 'running';

interface StatusBadgeProps {
  status: Status;
  label?: string;
  size?: 'sm' | 'md';
}

const STATUS_STYLES: Record<Status, { dot: string; bg: string; text: string }> = {
  success: {
    dot: 'bg-success',
    bg: 'bg-success/15',
    text: 'text-success',
  },
  warning: {
    dot: 'bg-warning',
    bg: 'bg-warning/15',
    text: 'text-warning',
  },
  error: {
    dot: 'bg-error',
    bg: 'bg-error/15',
    text: 'text-error',
  },
  info: {
    dot: 'bg-info',
    bg: 'bg-info/15',
    text: 'text-info',
  },
  running: {
    dot: 'bg-accent-purple',
    bg: 'bg-accent-purple/15',
    text: 'text-accent-purple-light',
  },
};

const SIZE_STYLES: Record<'sm' | 'md', { pill: string; dot: string; text: string }> = {
  sm: { pill: 'px-2 py-0.5 gap-1.5', dot: 'h-1.5 w-1.5', text: 'text-xs' },
  md: { pill: 'px-2.5 py-1 gap-2', dot: 'h-2 w-2', text: 'text-sm' },
};

export function StatusBadge({ status, label, size = 'sm' }: StatusBadgeProps) {
  const statusStyle = STATUS_STYLES[status];
  const sizeStyle = SIZE_STYLES[size];
  const displayLabel = label ?? status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <span
      className={`inline-flex items-center rounded-full ${statusStyle.bg} ${sizeStyle.pill}`}
    >
      <span
        className={`rounded-full ${statusStyle.dot} ${sizeStyle.dot} ${
          status === 'running' ? 'animate-pulse-dot' : ''
        }`}
      />
      <span className={`font-medium ${statusStyle.text} ${sizeStyle.text} leading-none`}>
        {displayLabel}
      </span>
    </span>
  );
}

export default StatusBadge;
